import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import ResponsiveClientLayout from "@/components/admin/ResponsiveClientLayout";
import { CreditoService, CreditoTransacao } from "@/services/creditoService";
import { StripeService, stripePromise } from "@/services/stripeService";
import { TaxCalculator, TaxCalculation } from "@/utils/taxCalculator";
import { ReembolsoService } from "@/services/reembolsoService";
import { useAuth } from "@/contexts/AuthContext";
import ModalReembolsoPix from "@/components/ui/ModalReembolsoPix";
import ModalVisualizarReembolso from "@/components/ui/ModalVisualizarReembolso";
import { 
  Plus, TrendingUp, TrendingDown, RefreshCw,
  AlertCircle, CheckCircle, Coins, Loader2, Eye, ArrowRight, Calendar,
  CreditCard, Smartphone
} from "lucide-react";

interface CreditTransaction {
  id: number;
  tipo: "compra" | "uso" | "estorno" | "reembolso" | "ajuste";
  valor: number;
  saldo_anterior: number;
  saldo_posterior: number;
  data_criacao: string;
  observacao: string;
  reserva_id?: number;
  pagamento_id?: string;
}

interface RefundRequest {
  id: string;
  valor: number;
  motivo: string;
  status: "PENDENTE" | "APROVADO" | "REJEITADO" | "PROCESSADO";
  created_at: string;
  data_processamento?: string;
  observacao_admin?: string;
  comprovante_pix?: string; // JSON string
}

const ClientCreditos = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [currentBalance, setCurrentBalance] = useState(0);
  const [transactions, setTransactions] = useState<CreditoTransacao[]>([]);
  const [refundRequests, setRefundRequests] = useState<RefundRequest[]>([]);
  const [purchaseAmount, setPurchaseAmount] = useState("");
  const [isPurchaseDialogOpen, setIsPurchaseDialogOpen] = useState(false);
  const [isReembolsoPixModalOpen, setIsReembolsoPixModalOpen] = useState(false);
  const [modalVisualizarReembolso, setModalVisualizarReembolso] = useState<{
    isOpen: boolean;
    reembolso: (RefundRequest & { cpf_chave_pix: string }) | null;
  }>({
    isOpen: false,
    reembolso: null
  });
  // Carregamento inicial da página
  const [initialLoading, setInitialLoading] = useState(true);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [paymentIntentId, setPaymentIntentId] = useState("");
  // ✅ CORREÇÃO: Fixar método de pagamento apenas como CARTAO (removido PIX)
  const metodoPagamento = 'CARTAO';
  const [calculoTaxas, setCalculoTaxas] = useState<TaxCalculation | null>(null);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // 🔧 FIX: Memoizar opções do Stripe Elements com mais estabilidade
  const stripeOptions = useMemo(() => {
    if (!clientSecret) return undefined;
    
    return {
      clientSecret,
      appearance: { theme: 'stripe' as const },
      locale: 'pt-BR' as const,
    };
  }, [clientSecret]);

  useEffect(() => {
    const state = location.state as { openPurchase?: boolean; openRefund?: boolean } | null;
    if (state?.openPurchase) {
      setIsPurchaseDialogOpen(true);
      window.history.replaceState({}, document.title);
      return;
    }
    if (state?.openRefund) {
      setIsReembolsoPixModalOpen(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);



  // Calcular taxas quando valor ou método de pagamento mudarem
  useEffect(() => {
    if (purchaseAmount && parseFloat(purchaseAmount) > 0) {
      const valor = parseFloat(purchaseAmount);
      const calculo = TaxCalculator.calcularTaxas(valor, metodoPagamento);
      setCalculoTaxas(calculo);
    } else {
      setCalculoTaxas(null);
    }
  }, [purchaseAmount, metodoPagamento]);

  // Funções para controlar toasts
  const showSuccessMessage = (message: string) => {
    setToastMessage(message);
    setShowSuccessToast(true);
    setTimeout(() => setShowSuccessToast(false), 4000);
  };

  const showErrorMessage = (message: string) => {
    setToastMessage(message);
    setShowErrorToast(true);
    setTimeout(() => setShowErrorToast(false), 4000);
  };

  const carregarDados = React.useCallback(async (isInitial: boolean = false) => {
    try {
      if (isInitial) {
        setInitialLoading(true);
      }
      

      // Carregar saldo
      const saldo = await CreditoService.getSaldoUsuario(user!.id);
      setCurrentBalance(saldo);

      // Carregar transações
      const transacoesResponse = await CreditoService.getTransacoesByUsuario(user!.id, 1, 50);
      setTransactions(transacoesResponse.transacoes);

      // Carregar solicitações de reembolso
      const reembolsosResponse = await ReembolsoService.getReembolsosByUsuario(user!.id);
      setRefundRequests(reembolsosResponse.reembolsos || []);

      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      if (isInitial) {
        setInitialLoading(false);
      }
    }
  }, [user]);

  // Carregar dados do usuário
  // Executa apenas quando o ID realmente muda (e não em cada atualização do objeto)
  useEffect(() => {
    if (user?.id) {
      carregarDados(true); // primeira vez
    }
  }, [user?.id, carregarDados]);



  const getTransactionIcon = (tipo: string) => {
    switch (tipo) {
      case "COMPRA": return <Plus className="w-4 h-4 text-green-600" />;
      case "USO": return <TrendingDown className="w-4 h-4 text-red-600" />;
      case "ESTORNO": return <RefreshCw className="w-4 h-4 text-blue-600" />;
      case "REEMBOLSO": return <TrendingDown className="w-4 h-4 text-orange-600" />;
      case "AJUSTE": return <AlertCircle className="w-4 h-4 text-purple-600" />;
      default: return <Coins className="w-4 h-4 text-gray-600" />;
    }
  };

  const isCredito = (valor: number) => valor > 0;

  const getStatusColor = (status: string) => {
    switch (status) {
      case "APROVADO": return "bg-green-100 text-green-800";
      case "PENDENTE": return "bg-yellow-100 text-yellow-800";
      case "REJEITADO": return "bg-red-100 text-red-800";
      case "PROCESSADO": return "bg-blue-100 text-blue-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const handlePurchaseCredits = async () => {
    if (!purchaseAmount || parseFloat(purchaseAmount) < 10) {
      showErrorMessage('Valor mínimo é R$ 10,00');
      return;
    }

    if (!calculoTaxas) {
      showErrorMessage('Erro no cálculo de taxas. Tente novamente.');
      return;
    }

    try {
      setPurchaseLoading(true);
      console.log('Iniciando compra de créditos:', calculoTaxas);

      // Criar Payment Intent com o valor total (incluindo taxas)
      const response = await StripeService.criarPaymentIntent(
        calculoTaxas.valorTotal, // ✅ Usar valor total com taxas
        user!.id,
        {
          valorSolicitado: calculoTaxas.valorSolicitado.toString(),
          valorTotal: calculoTaxas.valorTotal.toString(),
          taxaTotal: calculoTaxas.taxaTotal.toString(),
          metodoPagamento: calculoTaxas.metodoPagamento
        }
      );

      setClientSecret(response.client_secret);
      setPaymentIntentId(response.payment_intent_id);
      
      
      // O Payment Intent será processado no componente StripeCheckout
    } catch (error: unknown) {
      console.error('Erro ao processar pagamento:', error);
      showErrorMessage(error instanceof Error ? error.message : 'Erro ao processar pagamento');
    } finally {
      setPurchaseLoading(false);
    }
  };

  const handleReembolsoSuccess = async () => {
    
    showSuccessMessage('Solicitação de reembolso PIX enviada! O valor foi descontado do seu saldo. Você será notificado quando o PIX for processado.');
    
    // Recarregar dados
    await carregarDados();
  };

  // Componente interno para processar pagamento Stripe
  const StripeCheckout = ({ 
    onSuccess, 
    onError 
  }: { 
    onSuccess: (message: string) => void; 
    onError: (message: string) => void; 
  }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [processing, setProcessing] = useState(false);

    const handleSubmit = async (event: React.FormEvent) => {
      event.preventDefault();

      if (!stripe || !elements) {
        return;
      }

      setProcessing(true);

      try {
        
        
        const result = await StripeService.processarPagamentoComElements(
          stripe, 
          elements, 
          clientSecret
        );

        if (result.error) {
          
          onError(`Erro no pagamento: ${result.error.message}`);
        } else {
          
          onSuccess('Pagamento processado com sucesso! Seus créditos foram adicionados.');
          setIsPurchaseDialogOpen(false);
          setPurchaseAmount("");
          setClientSecret("");
          await carregarDados();
        }
      } catch (error: unknown) {
        console.error('Erro no pagamento Stripe:', error);
        
        // Tratamento específico para erros de integração Stripe
        const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
        if (errorMessage.includes('elements.submit()')) {
          onError('Erro na integração de pagamento. Tente novamente.');
        } else {
          onError(errorMessage || 'Erro no processamento do pagamento');
        }
      } finally {
        setProcessing(false);
      }
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <PaymentElement />
        <Button 
          type="submit" 
          disabled={!stripe || processing}
          className="w-full"
        >
          {processing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processando...
            </>
          ) : calculoTaxas ? (
            `Confirmar Pagamento ${TaxCalculator.formatarValor(calculoTaxas.valorTotal)}`
          ) : (
            `Pagar R$ ${parseFloat(purchaseAmount).toFixed(2)}`
          )}
        </Button>
      </form>
    );
  };



  if (initialLoading) {
    return (
      <ResponsiveClientLayout
        title="Minha Carteira"
        subtitle="Gerencie seus créditos e histórico de transações"
      >
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </ResponsiveClientLayout>
    );
  }

  return (
    <ResponsiveClientLayout
      title="Minha Carteira"
      subtitle="Gerencie seus créditos e histórico de transações"
    >
      {/* ✅ MELHORIA: Saldo Widget destacado com animação */}
      {/* <SaldoWidget 
        size="large" 
        showActions={true}
        className="mb-6"
        valor={currentBalance}
      /> */}



      <div className="mb-6 flex items-center justify-end">
        <div className="flex gap-3">
          <Dialog open={isPurchaseDialogOpen} onOpenChange={setIsPurchaseDialogOpen}>
            <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Comprar Créditos</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    {!clientSecret ? (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="purchase-amount">Valor para adicionar ao saldo (R$)</Label>
                          <Input
                            id="purchase-amount"
                            type="number"
                            step="0.01"
                            min="10"
                            value={purchaseAmount}
                            onChange={(e) => setPurchaseAmount(e.target.value)}
                            placeholder="100.00"
                          />
                          <p className="text-xs text-gray-500">Valor mínimo: R$ 10.00</p>
                        </div>

                        {/* ✅ CORREÇÃO: Método de pagamento fixo (apenas cartão) */}
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="flex items-center gap-2">
                            <CreditCard className="w-5 h-5 text-blue-600" />
                            <span className="font-medium text-blue-900">Pagamento via Cartão de Crédito</span>
                          </div>
                          <p className="text-sm text-blue-700 mt-1">
                            Processamento seguro via Stripe
                          </p>
                        </div>

                        {/* ✅ NOVO: Breakdown das taxas */}
                        {calculoTaxas && (
                          <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                            <h4 className="font-medium text-gray-900">Resumo do Pagamento</h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Valor do saldo:</span>
                                <span className="font-medium">{TaxCalculator.formatarValor(calculoTaxas.valorSolicitado)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">
                                  Taxa cartão ({calculoTaxas.taxaPercentual.toFixed(2)}%{calculoTaxas.taxaFixa > 0 ? ` + ${TaxCalculator.formatarValor(calculoTaxas.taxaFixa)}` : ''}):
                                </span>
                                <span className="text-orange-600 font-medium">{TaxCalculator.formatarValor(calculoTaxas.taxaTotal)}</span>
                              </div>
                              <div className="border-t pt-2 flex justify-between">
                                <span className="font-medium text-gray-900">Total a pagar:</span>
                                <span className="font-bold text-lg">{TaxCalculator.formatarValor(calculoTaxas.valorTotal)}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="p-4 bg-blue-50 rounded-lg">
                          <p className="text-sm text-blue-800">
                            • O valor será adicionado ao seu saldo após a confirmação do pagamento<br/>
                            • As taxas são cobradas pelo Stripe e não entram no seu saldo<br/>
                            • Você receberá {purchaseAmount ? TaxCalculator.formatarValor(parseFloat(purchaseAmount)) : 'o valor solicitado'} em créditos
                          </p>
                        </div>
                        <div className="flex justify-end gap-3">
                          <Button variant="outline" onClick={() => setIsPurchaseDialogOpen(false)}>
                            Cancelar
                          </Button>
                          <Button onClick={handlePurchaseCredits} disabled={purchaseLoading || !calculoTaxas}>
                            {purchaseLoading ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Preparando...
                              </>
                            ) : calculoTaxas ? (
                              `Pagar ${TaxCalculator.formatarValor(calculoTaxas.valorTotal)} no Cartão`
                            ) : (
                              'Digite um valor'
                            )}
                          </Button>
                        </div>
                      </>
                    ) : (
                      <Elements 
                        stripe={stripePromise} 
                        options={stripeOptions}
                      >
                        <StripeCheckout 
                          onSuccess={showSuccessMessage}
                          onError={showErrorMessage}
                        />
                      </Elements>
                    )}
                  </div>
                </DialogContent>
          </Dialog>
        </div>
      </div>



          {/* Refund Requests */}
          {refundRequests.length > 0 && (
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Solicitações de Reembolso</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {refundRequests.map((request) => {
                    const comprovante = request.comprovante_pix ? JSON.parse(request.comprovante_pix) : null;
                    
                    return (
                      <div key={request.id} className="border rounded-lg overflow-hidden">
                        <div className="flex items-center justify-between p-4">
                          <div className="flex-1">
                            <p className="font-medium">R$ {request.valor.toFixed(2)}</p>
                            <p className="text-sm text-gray-600">{request.motivo}</p>
                            <p className="text-xs text-gray-500">
                              Solicitado em {new Date(request.created_at).toLocaleDateString('pt-BR')}
                            </p>
                            {request.data_processamento && (
                              <p className="text-xs text-green-600">
                                Processado em {new Date(request.data_processamento).toLocaleDateString('pt-BR')}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            {request.status === 'PROCESSADO' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setModalVisualizarReembolso({
                                  isOpen: true,
                                  reembolso: { ...request, cpf_chave_pix: "" }
                                })}
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                Ver Detalhes
                              </Button>
                            )}
                            <Badge className={getStatusColor(request.status)}>
                              {request.status}
                            </Badge>
                          </div>
                        </div>
                        


                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Transaction History */}
          <Card>
            <CardHeader>
              <CardTitle>Histórico de Transações</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Saldo Anterior</TableHead>
                    <TableHead>Crédito/Débito</TableHead>
                    <TableHead>Novo Saldo</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Descrição</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getTransactionIcon(transaction.tipo)}
                          <span className="capitalize font-medium">{transaction.tipo}</span>
                        </div>
                      </TableCell>
                      <TableCell>R$ {transaction.saldo_anterior.toFixed(2)}</TableCell>
                      <TableCell>
                        <span className={`font-medium ${isCredito(transaction.valor) ? 'text-blue-600' : 'text-red-600'}`}>
                          {isCredito(transaction.valor) ? '+' : '-'}R$ {Math.abs(transaction.valor).toFixed(2)}
                        </span>
                      </TableCell>
                      <TableCell>R$ {transaction.saldo_posterior.toFixed(2)}</TableCell>
                      <TableCell>
                        {CreditoService.formatarData(transaction.created_at)}
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <p className="text-sm text-gray-600 truncate">{transaction.observacao}</p>
                        {transaction.reserva_id && (
                          <p className="text-xs text-blue-600">Reserva #{transaction.reserva_id}</p>
                        )}
                        {transaction.pagamento_id && (
                          <p className="text-xs text-green-600">ID: {transaction.pagamento_id}</p>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* 🆕 Modal de Reembolso PIX */}
          <ModalReembolsoPix
            isOpen={isReembolsoPixModalOpen}
            onClose={() => setIsReembolsoPixModalOpen(false)}
            saldoDisponivel={currentBalance}
            cpfUsuario={user?.cpf || ''}
            onSuccess={handleReembolsoSuccess}
            userId={user?.id || ''}
          />

          {/* 🆕 Modal para Visualizar Detalhes do Reembolso */}
          <ModalVisualizarReembolso
            isOpen={modalVisualizarReembolso.isOpen}
            onClose={() => setModalVisualizarReembolso({ isOpen: false, reembolso: null })}
            reembolso={modalVisualizarReembolso.reembolso}
          />

          {/* Toast de Sucesso */}
          {showSuccessToast && (
            <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-2 duration-300">
              <div className="bg-green-600 text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 max-w-md">
                <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                  <CheckCircle className="w-4 h-4" />
                </div>
                <span className="font-medium">{toastMessage}</span>
              </div>
            </div>
          )}

          {/* Toast de Erro */}
          {showErrorToast && (
            <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-2 duration-300">
              <div className="bg-red-600 text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 max-w-md">
                <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center">
                  <AlertCircle className="w-4 h-4" />
                </div>
                <span className="font-medium">{toastMessage}</span>
              </div>
            </div>
          )}
    </ResponsiveClientLayout>
  );
};

export default ClientCreditos; 
